'use client';

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import styles from './icon-button.module.css';
import { Spinner } from './spinner';

export type IconButtonSize = 'sm' | 'md';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: an icon-only button must name itself for screen readers. */
  label: string;
  /** The icon (from `@/components/icons`). */
  children: ReactNode;
  /** Square box matching the control heights: `sm` (default) fills a row of
   *  sm controls, `md` a row of md controls. */
  size?: IconButtonSize;
  tone?: 'default' | 'danger';
  /** Busy state (a pending `useTransition`): forces `disabled`, sets
   *  `aria-busy`, and swaps the icon for a Spinner. Same canonical affordance
   *  as Button's `pending`. */
  pending?: boolean;
}

/**
 * The reusable icon-only button: a square control box on the shared control
 * metrics (it sits flush beside Button/Input/Select of the same size), with
 * the shared focus ring, hover, and a11y (the icon carries no text, so
 * `label` becomes both aria-label and the tooltip). Extra button props pass
 * through (e.g. `onPointerDown` to stop a drag), and `className` merges so a
 * caller can add context styling (reveal-on-hover, placement).
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, children, size = 'sm', tone, pending, className, type, title, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={[styles.btn, className].filter(Boolean).join(' ')}
      data-size={size}
      data-tone={tone}
      data-pending={pending || undefined}
      aria-label={label}
      title={title ?? label}
      disabled={disabled || pending || undefined}
      aria-busy={pending || undefined}
      {...rest}
    >
      {pending ? <Spinner /> : children}
    </button>
  );
});
